import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiButton,
  MiCard,
  MiCheckbox,
  MiDetailFields,
  MiIcon,
  MiInputTextbox,
  MiMenu,
  MiModal,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  INPUT_TYPES,
  SECTION_HEADER_VARIANTS,
} from "@miplatform/constants";
import { useModal } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiBankOutline,
  mdiFileDocumentOutline,
  mdiKeyRemove,
  mdiPlus,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ConfigPageLayout } from "../../Config/ConfigPageLayout";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { QB_MAPPING_STRINGS, QB_MAPPING_TYPES } from "./constants";
import { useManageQuickBooksIntegrationViewModel } from "./useManageQuickBooksIntegrationViewModel";

const qBOItemMappingSchema = z.object({
  name: z.string().min(1, "Name is required"),
  qboItemId: z.string().min(1, "QuickBooks Item is required"),
  qboItemName: z.string().min(1, "QuickBooks Item Name is required"),
});

export function ManageQuickBooksIntegration() {
  const { classes, theme } = useStyles();
  const { isModalOpen, openModal, closeModal } = useModal();

  const {
    isLoading,
    quickBooksAuthConfig,
    getQuickBooksAuthConfig,
    handleDisconnect,
    createQBOItemMapping,
    getQBOItemMappings,
    qboItems,
    getQboItems,
    updateQBOItemMapping,
    deleteQBOItemMapping,
    bankAccounts,
    getBankAccounts,
    updateBankAccount,
    updateLocalBankAccountState,
    getOriginalBankAccount,
    getQboCustomFields,
    qboCustomFields,
    creditMemoMappings,
    customFieldMappings,
  } = useManageQuickBooksIntegrationViewModel();

  const [editingTemplate, setEditingTemplate] = useState(null);
  const [mappingMode, setMappingMode] = useState(
    QB_MAPPING_TYPES.Backcharge.value
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,

    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      qboItemId: "",
      qboItemName: "",
    },
    resolver: zodResolver(qBOItemMappingSchema),
  });

  useEffect(() => {
    getQuickBooksAuthConfig();
    getQBOItemMappings();
    getQboItems();
    getQboCustomFields();
    getBankAccounts();
  }, []);

  const handleBankAccountUpdate = async (account, field, value) => {
    const updatedData = {
      ...account,
      [field]: value,
    };
    await updateBankAccount(account.id, updatedData, account.modifiedOn);
  };

  const creditMemoFormFields = useMemo(() => {
    const isCustomField = mappingMode === "InvoiceBillingCommitmentCustomField";
    const options =
      (isCustomField ? qboCustomFields : qboItems)?.map((item) => ({
        value: item.id,
        label: item.name,
      })) || [];

    return [
      {
        label: QB_MAPPING_STRINGS.labels.mappingType,
        name: "name",
        fieldType: INPUT_TYPES.SELECT,
        options: [
          QB_MAPPING_TYPES.Backcharge,
          QB_MAPPING_TYPES.InvoiceBillingCommitmentCustomField,
        ],
        width: "100%",
        required: true,
        disabled: !!editingTemplate,
        handleChange: (event) => {
          const next = event.target.value;
          setMappingMode(next);
          setValue("name", next, { shouldDirty: true, shouldValidate: true });
          // Clear selection when switching type for correctness
          setValue("qboItemId", "", {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue("qboItemName", "", {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        label: isCustomField
          ? QB_MAPPING_STRINGS.labels.quickBooksCustomField
          : QB_MAPPING_STRINGS.labels.quickBooksItem,
        name: "qboItemId",
        fieldType: INPUT_TYPES.SELECT,
        width: "100%",
        required: true,
        options,
        handleChange: (event) => {
          const { value } = event.target;

          const list = isCustomField ? qboCustomFields : qboItems;
          const selectedItem = list.find((item) => item.id === value);
          if (selectedItem) {
            setValue("qboItemName", selectedItem.name);
            setValue("qboItemId", selectedItem.id, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        },
      },
    ];
  }, [mappingMode, qboItems, qboCustomFields, editingTemplate, setValue]);

  const handleModalClose = () => {
    reset({
      name: "",
      qboItemId: "",
      qboItemName: "",
    });
    setEditingTemplate(null);
    closeModal();
  };

  const onSubmitCreditMemo = async (data) => {
    const templateData = {
      ...data,
    };

    if (editingTemplate?.id) {
      await updateQBOItemMapping(
        editingTemplate.id,
        templateData,
        editingTemplate.modifiedOn
      );
    } else {
      await createQBOItemMapping(templateData);
    }

    handleModalClose();
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setMappingMode(template.name);
    reset({
      name: template.name,
      qboItemId: template.qboItemId,
      qboItemName: template.qboItemName,
    });
    openModal();
  };

  const handleDeleteTemplate = async (template) => {
    await deleteQBOItemMapping(template);
  };

  const renderItemActions = (item) => (
    <Box marginLeft="auto" onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdi.mdiPencilOutline,
            action: () => handleEditTemplate(item),
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdi.mdiTrashCanOutline,
            action: () => handleDeleteTemplate(item),
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  return (
    <ConfigPageLayout
      title="QuickBooks Integration"
      loading={isLoading}
      hideOnBack
    >
      <Box className={classes.contentContainer}>
        {/* QuickBooks Connection Section */}

        <ConfigAccordionHeader title="QuickBooks Connection">
          <MiSectionHeader
            subtitle="View your quickbooks connection details"
            leftIcon={mdi[ICONS_WEB.ACCOUNT_MULTIPLE]}
            variant={SECTION_HEADER_VARIANTS.UNDERLINED}
            titleVariant="h6"
          />
          <MiCard
            leftIcon={mdi[ICONS_WEB.ACCOUNT_MULTIPLE]}
            title="Connection Status:"
            titleStyles={{ fontSize: "18px" }}
            containerStyles={{ margin: "0 0 24px 0", padding: "16px" }}
          >
            {quickBooksAuthConfig?.isConnected && (
              <>
                <Box className={classes.checkCircle}>
                  <MiIcon
                    path={mdi[ICONS_WEB.CHECK_CIRCLE]}
                    iconSize="40px"
                    color="green"
                  />
                  <Typography variant="body2" fontSize="18px">
                    You are connected to QuickBooks.
                  </Typography>
                </Box>
                {quickBooksAuthConfig?.companyInfo && (
                  <Box className={classes.companyInfoBox}>
                    <Box className={classes.companyInfoRow}>
                      <Typography className={classes.companyInfoLabel}>
                        Legal Name:
                      </Typography>
                      <Typography className={classes.companyInfoValue}>
                        {quickBooksAuthConfig.companyInfo.legalName || "N/A"}
                      </Typography>
                    </Box>
                    <Box className={classes.companyInfoRow}>
                      <Typography className={classes.companyInfoLabel}>
                        Address:
                      </Typography>
                      <Typography className={classes.companyInfoValue}>
                        {[
                          quickBooksAuthConfig.companyInfo.city,
                          quickBooksAuthConfig.companyInfo.state,
                          quickBooksAuthConfig.companyInfo.postalCode,
                          quickBooksAuthConfig.companyInfo.country,
                        ]
                          .filter(Boolean)
                          .join(", ") || "N/A"}
                      </Typography>
                    </Box>
                    <Box className={classes.companyInfoRow}>
                      <Typography className={classes.companyInfoLabel}>
                        Email:
                      </Typography>
                      <Typography className={classes.companyInfoValue}>
                        {quickBooksAuthConfig.companyInfo.email || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box className={classes.actionContainer}>
                  <MiButton
                    color={BUTTON_COLORS.RED}
                    variant={BUTTON_VARIANTS.CONTAINED}
                    startIcon={{
                      path: mdiKeyRemove,
                      iconSize: "20px",
                    }}
                    onClick={handleDisconnect}
                  >
                    Disconnect
                  </MiButton>
                </Box>
              </>
            )}
            {!isLoading &&
              !quickBooksAuthConfig?.isConnected &&
              quickBooksAuthConfig?.authorizationUrl && (
                <>
                  <Typography className={classes.connectionText}>
                    To integrate with QuickBooks, please click the button below
                    to authenticate your account.
                  </Typography>
                  <Box className={classes.actionContainer}>
                    <a href={quickBooksAuthConfig?.authorizationUrl}>
                      <img
                        className={classes.qbLogo}
                        src="https://static.developer.intuit.com/images/C2QB_auth.png"
                        alt="Connect to QuickBooks Button"
                      />
                    </a>
                  </Box>
                </>
              )}
            {!isLoading && !quickBooksAuthConfig && (
              <Typography>Failed to load QuickBooks config.</Typography>
            )}
          </MiCard>
        </ConfigAccordionHeader>

        <ConfigAccordionHeader title="QuickBooks Mapping">
          <MiSectionHeader
            subtitle="Create and manage templates for mapping MiBuild items to Quickbooks items"
            leftIcon={mdiFileDocumentOutline}
            variant={SECTION_HEADER_VARIANTS.UNDERLINED}
            titleVariant="h6"
            rightAction={
              <MiButton
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                startIcon={{
                  path: mdiPlus,
                }}
                onClick={openModal}
              >
                Create Template
              </MiButton>
            }
          />

          <MiCard
            leftIcon={mdiFileDocumentOutline}
            title={QB_MAPPING_STRINGS.creditMemoTitle}
            containerStyles={{ margin: "0 0 24px 0", padding: "16px" }}
          >
            <Typography>{QB_MAPPING_STRINGS.creditMemoDescription}</Typography>
            {creditMemoMappings.length > 0 && (
              <Box sx={{ marginTop: 3 }}>
                <Typography variant="body1" fontWeight="600">
                  {QB_MAPPING_STRINGS.creditMemoExistingTitle}
                </Typography>
                {creditMemoMappings.map((template) => (
                  <Box
                    key={template.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      marginTop: 2,
                      padding: "8px 12px",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Typography variant="body2" fontSize="18px">
                      <strong>{QB_MAPPING_STRINGS.labels.miBuild}</strong>{" "}
                      {template.name}
                    </Typography>
                    <MiIcon path={mdi.mdiLink} iconSize="16px" size={0.8} />
                    <Typography variant="body2" fontSize="18px">
                      <strong>{QB_MAPPING_STRINGS.labels.quickBooks}</strong>{" "}
                      {template.qboItemName}
                    </Typography>
                    {renderItemActions(template)}
                  </Box>
                ))}
              </Box>
            )}
          </MiCard>

          <MiCard
            leftIcon={mdiFileDocumentOutline}
            title={QB_MAPPING_STRINGS.customFieldsTitle}
            containerStyles={{ margin: "0 0 24px 0", padding: "16px" }}
          >
            <Typography>
              {QB_MAPPING_STRINGS.customFieldsDescription}
            </Typography>
            {customFieldMappings.length > 0 && (
              <Box sx={{ marginTop: 3 }}>
                <Typography variant="body1" fontWeight="600">
                  {QB_MAPPING_STRINGS.customFieldsExistingTitle}
                </Typography>
                {customFieldMappings.map((template) => (
                  <Box
                    key={template.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      marginTop: 2,
                      padding: "8px 12px",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Typography variant="body2" fontSize="18px">
                      <strong>{QB_MAPPING_STRINGS.labels.miBuild}</strong>{" "}
                      {template.name}
                    </Typography>
                    <MiIcon path={mdi.mdiLink} iconSize="16px" size={0.8} />
                    <Typography variant="body2" fontSize="18px">
                      <strong>{QB_MAPPING_STRINGS.labels.quickBooks}</strong>{" "}
                      {template.qboItemName}
                    </Typography>
                    {renderItemActions(template)}
                  </Box>
                ))}
              </Box>
            )}
          </MiCard>
        </ConfigAccordionHeader>

        <ConfigAccordionHeader title="Bank Account Management">
          <MiSectionHeader
            subtitle="Choose which bank accounts can receive payments and give them easy-to-remember names."
            leftIcon={mdiBankOutline}
            variant={SECTION_HEADER_VARIANTS.UNDERLINED}
            titleVariant="h6"
          />

          <MiCard
            leftIcon={mdiBankOutline}
            title="Bank Accounts"
            containerStyles={{ margin: "0 0 24px 0", padding: "16px" }}
          >
            {bankAccounts.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="h6" fontWeight="600">
                  No Bank Accounts Detected From Quickbooks
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add a bank account to Quickbooks to manage them here.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {bankAccounts.map((account) => (
                  <Box key={account.id} className={classes.bankAccountRow}>
                    <Box className={classes.bankAccountInfo}>
                      <Typography variant="body1" fontWeight="600">
                        {account.fullyQualifiedName}
                        {account.acctNum && ` (...${account.acctNum})`}
                      </Typography>
                    </Box>
                    <Box className={classes.bankAccountControls}>
                      <MiInputTextbox
                        label="Alias"
                        value={account.alias || account.name || ""}
                        placeholder="Alias"
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          const originalAccount = getOriginalBankAccount(
                            account.id
                          );
                          const originalValue = originalAccount?.alias || "";
                          if (newValue !== originalValue) {
                            handleBankAccountUpdate(account, "alias", newValue);
                          }
                        }}
                        handleChange={(e) => {
                          updateLocalBankAccountState(
                            account.id,
                            "alias",
                            e.target.value
                          );
                        }}
                        sx={{ width: 280 }}
                      />
                      <MiCheckbox
                        label="Allow Payment Posting"
                        value={account.active || false}
                        handleChange={(e) => {
                          handleBankAccountUpdate(
                            account,
                            "active",
                            e.target.checked
                          );
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </MiCard>
        </ConfigAccordionHeader>

        <MiModal
          open={isModalOpen}
          closeModal={handleModalClose}
          title={
            editingTemplate
              ? "Edit Quickbooks Mapping"
              : "Create Quickbooks Mapping"
          }
          description={QB_MAPPING_STRINGS.modal.description}
          titleIcon={mdiFileDocumentOutline}
          width={500}
          actions={[
            {
              name: QB_MAPPING_STRINGS.modal.cancel,
              variant: BUTTON_VARIANTS.TEXT,
              color: BUTTON_COLORS.BLACK,
              onClick: handleModalClose,
            },
            {
              name: editingTemplate ? "Save Changes" : "Create Template",
              variant: BUTTON_VARIANTS.CONTAINED,
              color: BUTTON_COLORS.PRIMARY,
              onClick: handleSubmit(onSubmitCreditMemo),
              disabled: !isValid || !isDirty,
            },
          ]}
        >
          <MiDetailFields
            detailFields={creditMemoFormFields}
            control={control}
            customWrapperStyle={{ gap: 8 }}
          />
        </MiModal>
      </Box>
    </ConfigPageLayout>
  );
}

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    height: "inherit",
    overflow: "auto",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
  },
  checkCircle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  companyInfoBox: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.light}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.lightest,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  companyInfoRow: {
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  companyInfoLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "18px",
  },
  companyInfoValue: {
    color: theme.palette.text.secondary,
    fontSize: "18px",
  },
  actionContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: theme.spacing(3),
    gap: theme.spacing(2),
  },
  connectionText: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  qbLogo: {
    width: "275px",
    cursor: "pointer",
  },
  bankAccountRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.light}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.lightest,
  },
  bankAccountInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  bankAccountControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    textAlign: "center",
  },
}));
